import { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "@context/AuthProvider";
import { mapRole } from "@service/roleService";

import { useAccountQuery } from "@query/accountQuery";
import { useClubQuery } from "@query/clubQuery";
import { useSitesQuery } from "@query/siteQuery";
import { useRoleQuery } from "@query/roleQuery";

import { AlertContext } from "@context/AlertProvider";
import LoadingPage from "@templates/shared-theme/pages/LoadingPage";
import { useLogoutMutation } from "@mutation/authMutation";
import { useTranslation } from "react-i18next";

export const AccountContext = createContext(null);

export const AccountProvider = ({ children }) => {
  const { isLoggedIn } = useContext(AuthContext);
  
  if (!isLoggedIn) {
    return (
      <AccountContext.Provider value={{ account: null, club: null, sites: null, role: null }}>
        {children}
      </AccountContext.Provider>
    );
  }

  return <AccountLoader>{children}</AccountLoader>;
};

const AccountLoader = ({ children}) => {
  const { addAlertBanner } = useContext(AlertContext);

  const accountQuery = useAccountQuery();
  const roleQuery = useRoleQuery();
  const clubQuery = useClubQuery();
  const sitesQuery = useSitesQuery(clubQuery.data?.data?.uuid);
    const { t } = useTranslation("auth");

  const [role, setRole] = useState(null);
  const { mutate: logout } = useLogoutMutation();

  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Check for errors in any of the queries
    const hasQueryError = [accountQuery, roleQuery, clubQuery, sitesQuery].some(query => query.error);

    // If there's a query error, set the error state
    if (hasQueryError) {
      setHasError(true);
    }

    // Check if role mapping fails and update error state
    if (roleQuery.data?.data) {
      const mappedRole = mapRole(roleQuery.data.data.name);
      if (!mappedRole) {
        setHasError(true); // Set error if role mapping fails
      } else {
        setRole(mappedRole);
      }
    }
  }, [accountQuery, roleQuery, clubQuery, sitesQuery]);

  useEffect(() => {
    if (hasError) {
      // Perform the logout and show the alert, but only once
      logout();
      addAlertBanner(t("signInAgain"), "info");
    }
  }, [hasError, logout, addAlertBanner]);

  // Show loading until all required data is available
  if (accountQuery.isLoading || roleQuery.isLoading || clubQuery.isLoading || sitesQuery.isLoading) {
      return <LoadingPage/>;
  }

  // Extract data
  const account = accountQuery.data?.data ?? null;
  const club = clubQuery.data?.data ?? null;
  const sites = sitesQuery.data?.data ?? null;

  return (
      <AccountContext.Provider value={{ account, club, sites, role }}>
          {children}
      </AccountContext.Provider>
  );
};